
import { defineComponent } from 'vue';
import { questionToAnchor } from '@/utils/faq-helper';
import { generateFaqRichData } from '@/utils/faq-rich-data';
import { parseHTMLContent } from '@/utils/parse-html-content';
import ExpansionPanels from '@/components/organisms/mortgage-rates/server-faqs.vue';

export default defineComponent({
  name: 'FaqSection',

  components: {
    ExpansionPanels,
  },

  props: {
    faqs: {
      type: Array,
      required: true,
    },

    heading: {
      type: String,
      default: '',
    },

    headingLevel: {
      type: Number,
      default: 2,
    },

    headingCssClass: {
      type: String,
      default: 'mb-3 text-h3 mb-md-8 mx-md-auto text-md-center text-md-h1',
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },
    isExpandableByDefault: {
      type: Boolean,
      default: false,
    },

    mustardBackgroundColor: {
      type: Boolean,
      default: false,
    },
    showCta: {
      type: Boolean,
      default: true,
    },
    allOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isFaqExpanded: false,
      mobilePanels: [],
      openPanel: -1,
    };
  },

  computed: {
    allPanels() {
      const { allPanels } = questionToAnchor(this.faqs);
      return allPanels;
    },
    fivePanels() {
      return this.allPanels.slice(0, 5);
    },
    faqContent() {
      if (this.isExpandableByDefault) {
        return this.isFaqExpanded ? this.allPanels : this.fivePanels;
      } else {
        return this.isDesktop ? this.allPanels : this.mobilePanels;
      }
    },
    headingText() {
      return this.heading || this.$t('heading');
    },
    shouldDisplayExpandButton() {
      const { isDesktop, allPanels, mobilePanels } = this;
      return (
        (!isDesktop && allPanels.length !== mobilePanels.length) ||
        this.isExpandableByDefault
      );
    },
  },

  created() {
    this.listenToHashChanges();
  },
  mounted() {
    const { halfThePanels } = questionToAnchor(this.faqs);
    this.mobilePanels = halfThePanels;

    if (this.allPanels.length < 6) this.mobilePanels = this.allPanels;
  },
  methods: {
    listenToHashChanges() {
      if (!process.browser) {
        return;
      }

      window.addEventListener('hashchange', () => {
        this.openPanelByHash();
      });
    },

    toggleFaq() {
      if (!this.isFaqExpanded) {
        this.showAll();
      } else {
        this.mobilePanels = this.fivePanels;
      }

      this.isFaqExpanded = !this.isFaqExpanded;
    },

    async openPanelByHash() {
      if (!process.browser) {
        return;
      }

      const fakeHash = '_';

      const hash = window.location.hash;

      if (!hash || hash === '#' || hash === fakeHash) {
        return;
      }

      this.mobilePanels = this.allPanels;

      const openPanel = this.allPanels.findIndex(
        ({ anchor = null }) => `#${anchor}` === hash,
      );

      if (openPanel === -1) {
        return;
      }

      this.openPanel = openPanel;

      // The nextTick is needed to wait for the panel to be opened
      await this.$nextTick();

      const panelAnchor = document.querySelectorAll(
        `a[name="${hash.replace('#', '')}"]`,
      )[0];

      if (panelAnchor) {
        // Set the hash to an non-existing value to make sure
        // the next click on the FAQ link will work since it relies on hash change
        window.location.hash = fakeHash;
        // Added for pages with not footer and the FAQ section is the last section
        setTimeout(() => {
          panelAnchor.scrollIntoView({ behavior: 'smooth' });
        }, 200);
      }
    },

    showAll() {
      this.mobilePanels = this.allPanels;
    },
  },

  jsonld() {
    if (!this.faqs || this.faqs.length === 0) {
      return {};
    }

    const formatted = this.faqs.map((faq) => ({
      title: parseHTMLContent(faq.question),
      // It's alright to not parse the answer as google renders html there.
      content: faq.answer,
    }));
    return generateFaqRichData(formatted);
  },
});
